/** Reusable practitioner-report architecture. New metrics plug in via ReportMetric. */

export const RETENTION_FOLLOW_UP_DAYS = 90;

export type ReportTypeId = 'practitioner';

export type RevenueBasis = 'invoiced' | 'paid';

export type ReportGrouping =
  | 'practitioner_service'
  | 'service_practitioner'
  | 'practitioner'
  | 'service';

export type ReportPeriodPreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type MetricCategory = 'revenue' | 'appointment' | 'patient' | 'service' | 'performance';

export type MetricFormat = 'integer' | 'currency' | 'percent' | 'decimal' | 'text';

export type AppointmentStatusFilter = 'completed' | 'upcoming' | 'cancelled' | 'no_show' | 'all';

export type NewVsReturningFilter = 'all' | 'new' | 'returning';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface ReportFilterState {
  period: ReportPeriodPreset;
  customFrom: string | null;
  customTo: string | null;
  practitionerIds: string[];
  serviceIds: string[];
  locationIds: string[];
  appointmentStatus: AppointmentStatusFilter;
  revenueBasis: RevenueBasis;
  grouping: ReportGrouping;
  patientIds: string[];
  invoiceStatuses: string[];
  paymentMethods: string[];
  newVsReturning: NewVsReturningFilter;
  includeCancelled: boolean;
  includeNoShows: boolean;
  minRevenueCents: number | null;
  maxRevenueCents: number | null;
}

export interface ReportColumn {
  id: string;
  metricId: string;
  label: string;
  format: MetricFormat;
  sortable: boolean;
}

export interface ReportMetric {
  id: string;
  labelKey: string;
  descriptionKey: string;
  category: MetricCategory;
  format: MetricFormat;
  available: boolean;
  /** Shown as a summary card when selected. */
  summary: boolean;
  /** Appears as a table column when selected. */
  column: boolean;
  groupable: boolean;
  aggregatable: boolean;
}

export interface ReportPreset {
  id: string;
  labelKey: string;
  descriptionKey: string;
  metricIds: string[];
  grouping: ReportGrouping;
}

export interface ReportDefinition {
  id: ReportTypeId;
  labelKey: string;
  descriptionKey: string;
  metrics: ReportMetric[];
  presets: ReportPreset[];
  groupings: ReportGrouping[];
  revenueBases: RevenueBasis[];
}

export type ReportFactSourceType =
  | 'invoice'
  | 'punch_card_redemption'
  | 'punch_card_sale'
  | 'direct_payment'
  | 'product_sale'
  | 'other';

export interface ReportFact {
  fact_kind: 'appointment' | 'product_sale';
  appointment_id: string | null;
  invoice_id: string | null;
  invoice_line_id: string | null;
  punch_card_id: string | null;
  clinic_id: string;
  clinic_name: string | null;
  practitioner_id: string | null;
  practitioner_name: string | null;
  service_id: string | null;
  service_name: string | null;
  patient_id: string | null;
  patient_name: string | null;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  appointment_status: string | null;
  visit_status: string | null;
  billing_method: string | null;
  billing_status: string | null;
  payment_method: string | null;
  invoice_status: string | null;
  currency: string;
  invoiced_amount_cents: number;
  paid_amount_cents: number;
  outstanding_amount_cents: number;
  discount_cents: number;
  refund_cents: number;
  product_sales_cents: number;
  commission_percent: number | null;
  compensation_model: string | null;
  is_new_patient: boolean;
  returned_to_clinic: boolean;
  returned_to_practitioner: boolean;
  patient_first_completed_at: string | null;
  last_in_period_at: string | null;
  issued_at: string | null;
  paid_at: string | null;
  source_type: ReportFactSourceType | null;
  clips_redeemed: number;
  package_clip_count: number | null;
  package_sale_amount_cents: number | null;
  revenue_per_clip_cents: number | null;
}

export type ReportValue = number | string | null;

export interface ReportRow {
  id: string;
  label: string;
  depth: number;
  grouping: 'practitioner' | 'service' | 'total' | 'subtotal';
  practitionerId: string | null;
  serviceId: string | null;
  values: Record<string, ReportValue>;
  children: ReportRow[];
  factCount: number;
}

export interface ReportSummaryCard {
  metricId: string;
  value: ReportValue;
}

export interface ReportResult {
  titleKey: string;
  period: DateRange;
  grouping: ReportGrouping;
  revenueBasis: RevenueBasis;
  metricIds: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportRow;
  summary: ReportSummaryCard[];
  facts: ReportFact[];
  currency: string;
  retentionFollowUpDays: number;
  empty: boolean;
}

export interface ReportTemplateConfig {
  metricIds: string[];
  filters: ReportFilterState;
  presetId?: string;
  /** Empty practitioner selection: flagged roles, or every active employee. */
  practitionerScope?: 'practitioners' | 'employees';
}

export interface SavedReportTemplate {
  id: string;
  clinic_id: string;
  created_by: string | null;
  name: string;
  report_type: string;
  config: ReportTemplateConfig;
  created_at: string;
  updated_at: string;
}

export interface CompensationRule {
  id: string;
  clinic_id: string;
  practitioner_id: string;
  service_id: string | null;
  compensation_model: 'percentage_of_revenue';
  commission_percent: number;
  effective_from: string;
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PractitionerWorkingHoursContext {
  practitionerId: string;
  workingMinutes: number;
  workingDays: number;
}
